import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCopy,
  faLocationDot,
  faMoneyBill1,
  faCalendarAlt,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";

const ViewVacancie = () => {
  const { t } = useTranslation();
  const { id } = useParams();

  // Безопасное получение данных пользователя
  const userData = useMemo(
    () => JSON.parse(localStorage.getItem("user") || "{}"),
    [],
  );
  const userType = userData.role || "guest";
  const userId = userData.id || localStorage.getItem("userId");

  const [vacancy, setVacancy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [similarVacancies, setSimilarVacancies] = useState([]);
  const [copied, setCopied] = useState(false);

  // Состояния для формы отклика
  const [responseForm, setResponseForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    resume: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Хелпер для парсинга JSON полей вакансии
  const safeJSON = (str) => {
    try {
      if (!str) return [];
      return typeof str === "string" ? JSON.parse(str) : str;
    } catch {
      return [];
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchVacancy = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `/api/vacancyview.php?id=${id}&role=${userType}`,
          {
            cache: "no-cache",
          },
        );

        const data = await response.json();

        if (data.success && isMounted) {
          setVacancy({
            ...data.vacancy,
            responsibilities: safeJSON(data.vacancy.responsibilities),
            requirements: safeJSON(data.vacancy.requirements),
            conditions: safeJSON(data.vacancy.conditions),
            considerAlso: safeJSON(data.vacancy.considerAlso),
            views: data.views || 0,
            responses: data.responses || 0,
          });
          if (data.similar) setSimilarVacancies(data.similar);
        } else {
          console.error("Server error:", data.message);
        }
      } catch (error) {
        console.error("Failed to fetch vacancy:", error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchVacancy();
    return () => {
      isMounted = false;
    };
  }, [id, userType]);

  const imageUrls = useMemo(
    () => ({
      background: vacancy?.background
        ? `/api/uploads/${vacancy.background}`
        : null,
      logo: vacancy?.companyLogo ? `/api/uploads/${vacancy.companyLogo}` : null,
    }),
    [vacancy],
  );

  const handleCopyId = () => {
    if (vacancy?.id) {
      navigator.clipboard.writeText(vacancy.id.toString());
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setResponseForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setResponseForm((prev) => ({ ...prev, resume: e.target.files[0] }));
  };

  const handleSubmitResponse = async (e) => {
    e.preventDefault();
    
    // Проверяем ID перед отправкой, чтобы сразу выдать понятную ошибку
    if (!vacancy?.id || !userId) {
      setSubmitStatus({ 
        type: "error", 
        message: !userId ? "Пожалуйста, войдите в аккаунт" : "Ошибка: ID вакансии не найден" 
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const formData = new FormData();
      formData.append("vacancy_id", vacancy.id);
      formData.append("user_id", userId);
      formData.append("company_id", vacancy.user_id);
      formData.append("vacancy_title", vacancy.workname);
      formData.append("name", responseForm.name);
      formData.append("email", responseForm.email);
      formData.append("phone", responseForm.phone);
      formData.append("message", responseForm.message);
      if (responseForm.resume) formData.append("resume", responseForm.resume);

      const response = await fetch("/api/submit_response.php", {
        method: "POST",
        body: formData,
      });

      // Проверяем, ответил ли сервер вообще (нет ли ошибки сервера 500)
      if (!response.ok) {
        throw new Error(`Ошибка сервера: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setSubmitStatus({ type: "success", message: t("response_sent") });
        setResponseForm({
          name: "",
          email: "",
          phone: "",
          message: "",
          resume: null,
        });
      } else {
        // Если PHP вернул success: false, передаем текст ошибки из PHP прямо в catch
        throw new Error(data.message);
      }
    } catch (error) {
      console.error("Критическая ошибка при отправке отклика:", error);
      
      // Проверяем, пришла ли текстовая ошибка от PHP, или это реальный сбой сети
      const errorMessage = error.message && error.message.includes("Failed to fetch")
        ? t("errortitle") // Показываем "Проверьте интернет", только если fetch реально упал
        : error.message;  // В остальных случаях выводим точный текст ошибки (например, ошибку из PHP)

      setSubmitStatus({ type: "error", message: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full h-[500px] flex justify-center items-center">
        <p className="text-[#006674] text-xl font-semibold animate-pulse">
          {t("loading")}...
        </p>
      </div>
    );
  }

  if (!vacancy) {
    return (
      <div className="w-full h-[500px] flex flex-col justify-center items-center">
        <p className="text-gray-500 text-xl">{t("vacancy_not_found")}</p>
        <Link to="/" className="mt-4 text-[#006674] underline">
          {t("back_to_home")}
        </Link>
      </div>
    );
  }

  return (
    <>
      <section className="max-w-7xl mx-auto opacity-0 animate-[show_1s_forwards] bg-white flex flex-col gap-3">
        {/* ХЕДЕР */}
        <div className="relative w-full h-[230px] max-md:h-[120px] border-b-4 border-[#006674]">
          {imageUrls.background ? (
            <img
              src={imageUrls.background}
              alt="background"
              className="size-full object-cover"
            />
          ) : (
            <div className="size-full bg-gray-400 flex justify-center items-center text-3xl text-white">
              No Background
            </div>
          )}

          {/* Логотип */}
          <div className="absolute top-44 max-md:top-20 left-6 z-10">
            <div className="w-[160px] h-[160px] max-md:w-[130px] max-md:h-[130px] rounded-xl shadow-2xl bg-white flex items-center justify-center border">
              {imageUrls.logo ? (
                <img
                  src={imageUrls.logo}
                  className="w-full h-full object-contain rounded-xl"
                  alt="company logo"
                />
              ) : (
                <span className="text-gray-500">No Logo</span>
              )}
            </div>
          </div>
        </div>

        <div className="h-24 max-md:h-20" />

        <h2 className="text-4xl max-sm:text-2xl font-semibold px-6">
          {vacancy.workname}
        </h2>

        <div className="px-6 pb-6 flex flex-col gap-3">
          <Link
            to={`/ViewCompanyPage/${vacancy.user_id}`}
            className="text-2xl hover:underline text-[#006674]"
          >
            {vacancy.companyName}
          </Link>

          <h3 className="text-xl text-gray-600 flex items-center gap-2">
            ID: {vacancy.id}
            <button onClick={handleCopyId} className="hover:text-[#006674]">
              <FontAwesomeIcon icon={faCopy} />
            </button>
            {copied && (
              <span className="text-sm text-[#006674]">{t("copied")}</span>
            )}
          </h3>

          <hr className="border-[#006674] my-3" />

          {/* Details */}
          <div className="grid grid-cols-1 gap-2">
            <p>
              <span className="font-semibold text-[#006674]">
                {t("industry")}:
              </span>{" "}
              {vacancy.industry1}
            </p>
            <p>
              <span className="font-semibold text-[#006674]">
                {t("jobplace")}:
              </span>{" "}
              {vacancy.workplace}
            </p>
            <p>
              <span className="font-semibold text-[#006674]">
                {t("EmploymentType")}:
              </span>{" "}
              {vacancy.type}
            </p>
            <p>
              <span className="font-semibold text-[#006674]">
                {t("Salary")}:
              </span>{" "}
              {vacancy.salary} {vacancy.currency}
            </p>
          </div>

          <hr className="border-[#006674] my-3" />

          {/* Description */}
          <div className="mt-4">
            <h2 className="text-xl font-bold text-[#006674] mb-2">
              {t("DESCRIPTION")}
            </h2>
            <p className="text-gray-700">
              {vacancy.description || "Нет описания"}
            </p>
          </div>

          {[
            { title: t("RESPONSIBILITIES"), list: vacancy.responsibilities },
            {
              title: t("REQUIREMENTSANDESSENTIALSKILLS"),
              list: vacancy.requirements,
            },
            { title: t("TERMS AND BENEFITS"), list: vacancy.conditions },
          ].map((block, i) => (
            <div key={i} className="mt-6">
              <h2 className="text-xl font-bold text-[#006674]">
                {block.title}
              </h2>
              <ul className="list-disc px-6 mt-2">
                {block.list.map((item, idx) => (
                  <li key={idx} className="text-gray-800">
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Bottom Block */}
          {userType === "company" ? (
            <div className="mt-10 p-6 bg-gray-50 rounded-2xl border border-gray-200">
              <h2 className="text-2xl font-bold text-[#006674] mb-4">
                {t("statistics")}
              </h2>
              <p>
                {t("views")}: {vacancy.views}
              </p>
              <p>
                {t("applications")}: {vacancy.responses}
              </p>
            </div>
          ) : (
            <div className="mt-10 p-6 bg-white border rounded-2xl shadow-lg">
              <h2 className="text-2xl font-bold text-[#006674] mb-4">
                {t("APPLY")}
              </h2>
              
              {submitStatus && (
                <div
                  className={`p-3 mb-4 rounded font-medium ${
                    submitStatus.type === "success" 
                      ? "bg-green-100 text-green-800 border border-green-200" 
                      : "bg-red-100 text-red-800 border border-red-200"
                  }`}
                >
                  {submitStatus.message}
                </div>
              )}

              <form
                onSubmit={handleSubmitResponse}
                className="flex flex-col gap-4"
              >
                <input
                  name="name"
                  placeholder={t("name")}
                  value={responseForm.name}
                  onChange={handleInputChange}
                  className="p-3 border rounded-xl outline-none focus:border-[#006674]"
                  required
                />
                <input
                  name="email"
                  type="email"
                  placeholder={t("email")}
                  value={responseForm.email}
                  onChange={handleInputChange}
                  className="p-3 border rounded-xl outline-none focus:border-[#006674]"
                  required
                />
                <input
                  name="phone"
                  placeholder={t("phone")}
                  value={responseForm.phone}
                  onChange={handleInputChange}
                  className="p-3 border rounded-xl outline-none focus:border-[#006674]"
                  required
                />
                <textarea
                  name="message"
                  placeholder={t("coverlater")}
                  value={responseForm.message}
                  onChange={handleInputChange}
                  className="p-3 border rounded-xl h-32 outline-none focus:border-[#006674]"
                  required
                />

                <div className="flex items-center gap-4">
                  <label
                    htmlFor="file"
                    className="bg-[#006674] text-white px-6 py-2 rounded-xl cursor-pointer transition hover:bg-[#005662]"
                  >
                    {t("UploadCV")}
                  </label>
                  <input
                    type="file"
                    id="file"
                    name="resume"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  {responseForm.resume && (
                    <span className="text-sm text-gray-500 font-medium">
                      {responseForm.resume.name}
                    </span>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-[#006674] text-white py-3 px-10 rounded-xl hover:bg-[#005662] disabled:opacity-50 font-semibold"
                >
                  {isSubmitting ? "..." : t("APPLY")}
                </button>
              </form>
            </div>
          )}
        </div>
      </section>

      {/* Similar Vacancies */}
      {similarVacancies.length > 0 && (
        <div className="max-w-7xl mx-auto mt-10 px-6">
          <h2 className="text-2xl font-bold mb-6">{t("simularjobs")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {similarVacancies.map((vac) => (
              <div
                key={vac.id}
                className="border rounded-xl p-4 shadow-sm hover:shadow-md transition"
              >
                <Link
                  to={`/vacancy/${vac.id}`}
                  className="text-[#006674] font-bold text-lg"
                >
                  {vac.workname}
                </Link>
                <p className="text-gray-500">{vac.companyName}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default ViewVacancie;